// src/utils/api.js
import axios from 'axios';
import useUserStore from '../stores/userStore';
import useSessionOrchestratorStore from '../auth/sessionOrchestrator/store';
import { resolveSessionAccessState } from '../auth/sessionOrchestrator/sessionAccessModel';
import { MESSAGES } from '../i18n/messages';
import { resolveLocale } from './locale';

/**
 * Normalise la baseURL :
 * - prend VITE_API_URL si défini (sans slash final)
 * - sinon fallback '/api'
 */
const normalizeApiUrl = () => {
  const envUrl = import.meta.env?.VITE_API_URL ? String(import.meta.env.VITE_API_URL).trim() : '';
  return envUrl ? envUrl.replace(/\/+$/, '') : '';
};

/**
 * Base URL normalisée :
 * - en DEV : utilise toujours `/api` pour profiter du proxy Vite (et tester sur mobile sans `localhost`)
 * - en PROD : utilise `VITE_API_URL` si défini, sinon fallback `/api`
 */
export const API_BASE = import.meta.env?.DEV ? '/api' : (normalizeApiUrl() || '/api');

const API_ERROR_CODE_TO_I18N_KEY = Object.freeze({
  ACTIVE_ORDERS: 'apiErrors.codes.ACTIVE_ORDERS',
  ACCOUNT_DELETED_OR_MISSING: 'apiErrors.codes.ACCOUNT_DELETED_OR_MISSING',
  EMAIL_NOT_VERIFIED: 'apiErrors.codes.EMAIL_NOT_VERIFIED',
  INVALID_TOKEN: 'apiErrors.codes.INVALID_TOKEN',
  SESSION_EXPIRED: 'apiErrors.codes.SESSION_EXPIRED',
  SUPPORT_COOLDOWN: 'apiErrors.codes.SUPPORT_COOLDOWN',
  SUPPORT_RATE_LIMIT_USER: 'apiErrors.codes.SUPPORT_RATE_LIMIT_USER',
  SUPPORT_RATE_LIMIT_IP: 'apiErrors.codes.SUPPORT_RATE_LIMIT_IP',
  PRELAUNCH_EMAIL_INVALID: 'apiErrors.codes.PRELAUNCH_EMAIL_INVALID',
  PRELAUNCH_SUBSCRIBE_FAILED: 'apiErrors.codes.PRELAUNCH_SUBSCRIBE_FAILED',
  AMBASSADOR_DETACH_FAILED: 'apiErrors.codes.AMBASSADOR_DETACH_FAILED',
  INVALID_TARGET_AMBASSADOR: 'apiErrors.codes.INVALID_TARGET_AMBASSADOR',
  VENDOR_ALREADY_ASSIGNED_TO_TARGET: 'apiErrors.codes.VENDOR_ALREADY_ASSIGNED_TO_TARGET',
  VENDOR_IDS_REQUIRED: 'apiErrors.codes.VENDOR_IDS_REQUIRED',
  VENDOR_BULK_LIMIT_EXCEEDED: 'apiErrors.codes.VENDOR_BULK_LIMIT_EXCEEDED',
  VENDOR_REASSIGN_BULK_FAILED: 'apiErrors.codes.VENDOR_REASSIGN_BULK_FAILED',
  LINK_SESSION_INVALID: 'apiErrors.codes.LINK_SESSION_INVALID',
  LINK_SESSION_EXPIRED: 'apiErrors.codes.LINK_SESSION_EXPIRED',
  LINK_CONFIRMATION_REQUIRED: 'apiErrors.codes.LINK_CONFIRMATION_REQUIRED',
  LINK_FORBIDDEN: 'apiErrors.codes.LINK_FORBIDDEN',
  LINK_FAILED: 'apiErrors.codes.LINK_FAILED',
  LINK_ALREADY_DONE: 'apiErrors.codes.LINK_ALREADY_DONE',
  LINK_RATE_LIMITED: 'apiErrors.codes.LINK_RATE_LIMITED',
  ORDER_CANCEL_CLIENT_PENDING_ONLY: 'apiErrors.codes.ORDER_CANCEL_CLIENT_PENDING_ONLY',
  ORDER_PAYMENT_CAPTURED_CANCEL_IMPOSSIBLE: 'apiErrors.codes.ORDER_PAYMENT_CAPTURED_CANCEL_IMPOSSIBLE',
  ORDER_CANCEL_FORBIDDEN: 'apiErrors.codes.ORDER_CANCEL_FORBIDDEN',
  ORDER_CANCEL_VENDOR_INACTIVE: 'apiErrors.codes.ORDER_CANCEL_VENDOR_INACTIVE',
  ORDER_CANCEL_SERVER_ERROR: 'apiErrors.codes.ORDER_CANCEL_SERVER_ERROR',
});

const getPathValue = (obj, path) => {
  if (!obj || typeof path !== 'string') return undefined;
  return path.split('.').reduce((acc, segment) => {
    if (!acc || typeof acc !== 'object') return undefined;
    return acc[segment];
  }, obj);
};

const interpolate = (template, values = {}) => {
  if (typeof template !== 'string') return '';
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_match, token) => {
    const v = values[token];
    if (v === null || typeof v === 'undefined') return '';
    return String(v);
  });
};

const isCodeLike = (value) => typeof value === 'string' && /^[A-Z0-9_]+$/.test(value.trim());

const getLocaleCandidates = (localeInput) => {
  const normalized = resolveLocale(localeInput || 'fr-FR');
  const base = normalized.split('-')[0];
  return Array.from(new Set([normalized, base, 'fr-FR', 'fr']));
};

const translateApiKey = (localeInput, key, values = {}, fallback = '') => {
  const candidates = getLocaleCandidates(localeInput);
  for (const candidate of candidates) {
    const template = getPathValue(MESSAGES[candidate], key);
    if (typeof template === 'string') {
      return interpolate(template, values);
    }
  }
  return fallback || key;
};

const pickApiErrorCode = (err) => {
  const payload = err?.response?.data || {};
  const candidates = [payload?.errorCode, payload?.code, payload?.error, err?.code];
  for (const candidate of candidates) {
    if (isCodeLike(candidate)) return candidate.trim();
  }
  return '';
};

const pickReadableApiMessage = (err) => {
  const payload = err?.response?.data || {};
  const maybe = [
    payload?.message,
    payload?.error,
    err?.message,
  ];
  for (const candidate of maybe) {
    if (!candidate || typeof candidate !== 'string') continue;
    const trimmed = candidate.trim();
    if (!trimmed) continue;
    if (isCodeLike(trimmed)) continue;
    return trimmed;
  }
  return '';
};

const getCurrentLocale = (preferred) => {
  const state = useUserStore.getState?.() || {};
  return resolveLocale(
    preferred ||
      state?.preferredLocale ||
      state?.userData?.preferredLocale ||
      (typeof navigator !== 'undefined' ? navigator.language : 'fr-FR') ||
      'fr-FR'
  );
};

export const resolveApiErrorMessage = (
  err,
  {
    locale,
    fallbackKey = '',
    fallbackMessage = 'Une erreur est survenue',
    values = {},
  } = {}
) => {
  if (!err) return fallbackMessage;
  if (err?.code === 'ERR_CANCELED') return '';

  const resolvedLocale = getCurrentLocale(locale);
  const payload = err?.response?.data || {};
  const code = pickApiErrorCode(err);
  const mappedKey =
    (typeof payload?.i18nKey === 'string' && payload.i18nKey.trim()) ||
    API_ERROR_CODE_TO_I18N_KEY[code] ||
    (code ? `apiErrors.codes.${code}` : '');
  const mergedValues = {
    ...values,
    ...(payload?.details && typeof payload.details === 'object' ? payload.details : {}),
    retryAfterSec: payload?.retryAfterSec,
    retryAfter: payload?.retryAfter,
  };

  if (mappedKey) {
    const translated = translateApiKey(resolvedLocale, mappedKey, mergedValues, '');
    if (translated && translated !== mappedKey) return translated;
  }

  if (fallbackKey) {
    const translatedFallback = translateApiKey(resolvedLocale, fallbackKey, mergedValues, '');
    if (translatedFallback && translatedFallback !== fallbackKey) return translatedFallback;
  }

  return pickReadableApiMessage(err) || fallbackMessage;
};

/* ------------------------------------------------------------------ */
/*                            TOKEN PROVIDER                           */
/* ------------------------------------------------------------------ */
/**
 * Le front enregistre ici une fonction asynchrone capable de fournir
 * un access token "frais" à la demande (ex: getAccessTokenSilently({cacheMode:'off'})).
 * Voir App.jsx → registerTokenProvider(() => ...).
 */
let _getFreshToken = null;
let refreshLock = null;
export function registerTokenProvider(fn) {
  const provider = typeof fn === 'function' ? fn : null;
  if (provider !== _getFreshToken) refreshLock = null;
  _getFreshToken = provider;
}

const sessionExpiredError = () => {
  const err = new Error('SESSION_EXPIRED');
  err.code = 'SESSION_EXPIRED';
  return err;
};

const hasAuthenticatedRuntime = (userState = {}) =>
  Boolean(userState?.auth0User || userState?.getTokenSilently || userState?.token);

const markSessionRecovery = (err, context) => {
  const state = useUserStore.getState?.() || {};
  if (err && state.handleAuth0TokenError?.(err, context)) return true;
  state.markSessionExpired?.({ reason: 'session_expired', context });
  return true;
};

const currentTokenOwner = () => {
  const state = useUserStore.getState();
  return state.auth0User?.sub || state.userData?.auth0Id || state.userData?._id || null;
};

/** Coalesce refreshes only within the same provider and authenticated identity. */
export async function getFreshTokenOnce() {
  const provider = _getFreshToken;
  if (!provider) return null;
  const owner = currentTokenOwner();
  if (refreshLock?.provider === provider && refreshLock?.owner === owner) {
    return refreshLock.promise;
  }
  const operation = { provider, owner, promise: null };
  const assertOwner = () => {
    if (_getFreshToken !== provider || currentTokenOwner() !== owner) {
      throw new axios.CanceledError('Authentication changed during token refresh');
    }
  };
  operation.promise = Promise.resolve().then(() => {
    assertOwner();
    return provider();
  }).then((token) => {
    assertOwner();
    if (token) useUserStore.getState().setToken?.(token);
    return token || null;
  }, (error) => {
    assertOwner();
    throw error;
  }).finally(() => {
    if (refreshLock === operation) refreshLock = null;
  });
  refreshLock = operation;
  return operation.promise;
}

/* ------------------------------------------------------------------ */
/*                             AXIOS CLIENT                            */
/* ------------------------------------------------------------------ */
// Permettre un mode cookies HttpOnly en prod si souhaité
// Mettre VITE_API_WITH_CREDENTIALS=true pour activer withCredentials (cookies cross-site)
const API_WITH_CREDENTIALS = String(import.meta.env?.VITE_API_WITH_CREDENTIALS || '').toLowerCase() === 'true';

export const apiClient = axios.create({
  baseURL: API_BASE,
  withCredentials: API_WITH_CREDENTIALS,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  // Timeout raisonnable pour éviter les requêtes pendantes
  timeout: 20000,
  // On laisse Axios gérer automatiquement JSON
});

if (import.meta?.env?.DEV) {
   
  console.log('[api] baseURL =', API_BASE, '| withCredentials =', API_WITH_CREDENTIALS);
}

// Bind offline mutations to the account that queued them, including token-refresh
// and 401 retry paths. This is local metadata, never an authentication header.
const assertRequestActor = (config) => {
  if (config.rtActorId === undefined) return;
  const actorId = useUserStore.getState().userData?._id;
  if (!config.rtActorId || !actorId || String(actorId) !== config.rtActorId) {
    throw new axios.CanceledError('Offline action belongs to a different account');
  }
};

/**
 * REQUEST INTERCEPTOR
 * - injecte Authorization depuis la source unique (userStore)
 * - si pas de token au tout début (course d'init), tente un fresh token
 */
apiClient.interceptors.request.use(
  async (config) => {
    assertRequestActor(config);
    config.headers = config.headers || {};
    const userState = useUserStore.getState();
    const preferredLocale =
      userState?.preferredLocale ||
      userState?.userData?.preferredLocale ||
      (typeof navigator !== 'undefined' ? navigator.language : 'fr-FR') ||
      'fr-FR';
    if (!config.headers['Accept-Language'] && !config.headers['accept-language']) {
      config.headers['Accept-Language'] = preferredLocale;
    }

    const sessionAccess = resolveSessionAccessState({
      userState,
      sessionState: useSessionOrchestratorStore.getState(),
    });
    if (sessionAccess.isBlocked) {
      return Promise.reject(sessionExpiredError());
    }

    // Si multipart FormData, laisser le navigateur définir le boundary
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }

    // Si déjà présent (ex: override ponctuel), ne touche pas
    if (config.headers.Authorization) return config;

    const token = userState.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    }

    // Pas de token en store → tentative d'obtention fraîche
    let fresh = null;
    try {
      fresh = await getFreshTokenOnce();
    } catch (err) {
      if (err?.code === 'ERR_CANCELED') throw err;
      if (hasAuthenticatedRuntime(userState)) {
        markSessionRecovery(err, 'api:request-token');
        return Promise.reject(sessionExpiredError());
      }
      throw err;
    }
    assertRequestActor(config);
    if (fresh) {
      config.headers.Authorization = `Bearer ${fresh}`;
      return config;
    }

    const nextSessionAccess = resolveSessionAccessState({
      userState: useUserStore.getState(),
      sessionState: useSessionOrchestratorStore.getState(),
    });
    if (nextSessionAccess.isBlocked || hasAuthenticatedRuntime(userState)) {
      if (!nextSessionAccess.isBlocked) {
        markSessionRecovery(null, 'api:missing-token');
      }
      return Promise.reject(sessionExpiredError());
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/* ------------------------------------------------------------------ */
/*                         RETRY (TRANSIENT)                           */
/* ------------------------------------------------------------------ */
const TRANSIENT_HTTP_STATUSES = new Set([502, 503, 504]);
const IDEMPOTENT_METHODS = new Set(['get', 'head']);
const MAX_TRANSIENT_RETRIES = 2;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const waitForOnline = async (maxMs = 5000) => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (navigator.onLine !== false) return true;

  return new Promise((resolve) => {
    let done = false;
    const finish = (value) => {
      if (done) return;
      done = true;
      try {
        window.removeEventListener('online', onOnline);
      } catch {
        // ignore
      }
      if (timeout) clearTimeout(timeout);
      resolve(value);
    };

    const onOnline = () => finish(true);
    const timeout = setTimeout(() => finish(false), maxMs);

    try {
      window.addEventListener('online', onOnline);
    } catch {
      finish(false);
    }
  });
};

const isIdempotent = (method) => IDEMPOTENT_METHODS.has(String(method || 'get').toLowerCase());

const shouldRetryTransient = (error, config) => {
  if (!config) return false;
  if (!isIdempotent(config.method)) return false;
  if (error?.code === 'ERR_CANCELED') return false;
  if (config.signal?.aborted) return false;

  const status = error?.response?.status;
  if (typeof status === 'number' && TRANSIENT_HTTP_STATUSES.has(status)) return true;
  if (!error?.response) return true; // réseau / offline / proxy reset
  return false;
};

/**
 * RESPONSE INTERCEPTOR
 * - sur 401 : tente UNE fois un refresh puis rejoue la requête
 * - n'entrave pas les autres erreurs
 */
apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;

    // Abort/cancel : ne jamais retry
    if (error?.code === 'ERR_CANCELED' || original?.signal?.aborted) {
      return Promise.reject(error);
    }

    if (!original || original.__rtRetried || status !== 401) {
      // Retry "transient" (reboot backend / réseau) pour les GET/HEAD.
      if (original && shouldRetryTransient(error, original)) {
        const attempt = Number(original.__rtTransientRetry || 0) + 1;
        if (attempt <= MAX_TRANSIENT_RETRIES) {
          original.__rtTransientRetry = attempt;

          // Si offline, on attend un court instant la reconnexion (sinon retry immédiat inutile).
          await waitForOnline(5000);

          // Backoff avec jitter (évite le thundering herd sur reboot)
          const jitter = Math.floor(Math.random() * 250);
          const delay = Math.min(2500, 400 * 2 ** (attempt - 1) + jitter);
          await sleep(delay);

          return apiClient.request(original);
        }
      }

      return Promise.reject(error);
    }

    original.__rtRetried = true;
    try {
      const fresh = await getFreshTokenOnce();
      if (fresh) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${fresh}`;
        return apiClient.request(original);
      }
    } catch (err) {
      if (err?.code === 'ERR_CANCELED') throw err;
      if (hasAuthenticatedRuntime(useUserStore.getState())) {
        markSessionRecovery(err, 'api:response-401');
        return Promise.reject(sessionExpiredError());
      }
      return Promise.reject(error);
    }
    if (hasAuthenticatedRuntime(useUserStore.getState())) {
      markSessionRecovery(error, 'api:response-401-missing-token');
      return Promise.reject(sessionExpiredError());
    }
    return Promise.reject(error);
  }
);

/* ------------------------------------------------------------------ */
/*                              HELPERS                                */
/* ------------------------------------------------------------------ */

/**
 * Gestion d’erreur simple + set({ error }) compatible Zustand
 */
export const handleAxiosError = (err, set, fallbackMessage = 'Une erreur est survenue') => {
  if (err?.code === 'ERR_CANCELED' || err?.code === 'SESSION_EXPIRED') return;

  // Log utile en dev
  if (import.meta?.env?.DEV) {
     
    console.error('❌ API ERROR:', err);
  }

  const msg = resolveApiErrorMessage(err, { fallbackMessage });

  set?.({ error: msg });
  return msg;
};

/**
 * Enveloppe d'exécution async avec gestion loading/error centralisée.
 * @param {Function} set - set() de Zustand
 * @param {() => Promise<any>} asyncFn - fonction asynchrone à exécuter
 * @param {{ silent?: boolean }} opts
 */
export const withLoadingAndError = async (set, asyncFn, opts = {}) => {
  const { silent = false } = opts;
  if (!silent) set?.({ loading: true, error: null });
  try {
    return await asyncFn();
  } catch (err) {
    handleAxiosError(err, set);
    throw err;
  } finally {
    if (!silent) set?.({ loading: false });
  }
};

/**
 * Transforme de possibles formes Mongo/ObjectId → plain values.
 */
export function transformUserDataFromStorage(userData) {
  if (!userData) return null;
  if (typeof userData._id === 'object' && userData._id?.$oid) {
    userData._id = userData._id.$oid;
  }
  if (!userData._id && userData.id) userData._id = userData.id;
  return userData;
}

/**
 * Construit un FormData à partir d'un objet.
 * - Série JSON seulement pour certains champs (ex: location, horaires)
 * - Exclusion de clés via options.exclude
 */
export function createFormData(data, options = { exclude: [], keepEmptyStringsFor: [] }) {
  const formData = new FormData();
  const keepEmptyStringsFor = new Set(options?.keepEmptyStringsFor || []);
  const isDomLike = (v) =>
    (typeof Element !== 'undefined' && v instanceof Element) ||
    (v && typeof v === 'object' && v.nodeType === 1);
  const isEventLike = (v) =>
    v &&
    typeof v === 'object' &&
    (typeof v.isPropagationStopped === 'function' ||
      typeof v.isDefaultPrevented === 'function' ||
      v.nativeEvent);
  const safeStringify = (key, value) => {
    try {
      return JSON.stringify(value);
    } catch (err) {
      if (import.meta?.env?.DEV) {
        console.warn('[api] skipped non-serializable field', key, err);
      }
      return null;
    }
  };

  for (const [key, value] of Object.entries(data || {})) {
    if (
      value === undefined ||
      value === null ||
      (typeof value === 'string' && value.trim() === '' && !keepEmptyStringsFor.has(key)) ||
      options.exclude.includes(key)
    ) {
      continue;
    }

    const isFileLike =
      (typeof File !== 'undefined' && value instanceof File) ||
      (typeof Blob !== 'undefined' && value instanceof Blob);
    if (!isFileLike && (isDomLike(value) || isEventLike(value))) {
      if (import.meta?.env?.DEV) {
        console.warn('[api] skipped DOM/event-like field', key);
      }
      continue;
    }
    const mustJson = ['location', 'horaires'].includes(key);
    const shouldJson =
      mustJson ||
      (
        !isFileLike &&
        typeof value === 'object' &&
        value !== null
      );
    if (shouldJson) {
      const json = safeStringify(key, value);
      if (json != null) formData.append(key, json);
    } else {
      if (import.meta?.env?.DEV && isFileLike) {
        console.debug('[api] append file', key, value?.name || null, value?.size || null, value?.type || null);
      }
      formData.append(key, value);
    }
  }

  // Ajoute explicitement boutiqueId si présent sous 'boutique'
  if (data?.boutique && !formData.has('boutiqueId')) {
    formData.append('boutiqueId', data.boutique);
  }

  return formData;
}

/** Variante sans fichiers (exclut coverImage) */
export const createFormDataSansImage = (data) =>
  createFormData(data, { exclude: ['coverImage'] });

/**
 * SWR minimaliste :
 * - Rend les données cache d'abord (si présentes),
 * - Revalide en arrière-plan avec If-None-Match/ETag,
 * - Appelle onData() à chaque mise à jour.
 *
 * @param {string} key
 * @param {(opts?: { headers?: object, validateStatus?: (code:number)=>boolean }) => Promise<import('axios').AxiosResponse>} fetchFn
 * @param {{ onData?: (data:any, meta?:{fromCache:boolean}) => void }} opts
 */
export async function swrFetch(key, fetchFn, { onData, onError } = {}) {
  // 1) Serve cache si dispo (ne doit jamais empêcher la revalidation, même si localStorage plante)
  try {
    const cached = localStorage.getItem(`swr:${key}`);
    if (cached) {
      try {
        let ts = null;
        try {
          const rawTs = localStorage.getItem(`swr:${key}:ts`);
          const n = rawTs ? Number(rawTs) : NaN;
          ts = Number.isFinite(n) ? n : null;
        } catch {
          ts = null;
        }
        onData?.(JSON.parse(cached), { fromCache: true, ts });
      } catch {
        // ignore cache parse error
      }
    }
  } catch {
    // iOS/PWA/private-mode: localStorage peut throw → on continue quand même
  }

  // 2) Revalidate (réseau)
  try {
    let etag = null;
    try {
      etag = localStorage.getItem(`swr:${key}:etag`);
    } catch {
      etag = null;
    }

    const headers = etag ? { 'If-None-Match': etag } : {};
    const validateStatus = (s) => (s >= 200 && s < 300) || s === 304;

    const res = await fetchFn({ headers, validateStatus });
    if (res.status === 304) return;

    const data = res.data;
    const newEtag = res.headers?.etag || res.headers?.ETag || res.headers?.ETAG;
    try {
      if (newEtag) localStorage.setItem(`swr:${key}:etag`, newEtag);
      localStorage.setItem(`swr:${key}:ts`, String(Date.now()));
      localStorage.setItem(`swr:${key}`, JSON.stringify(data));
    } catch {
      // storage indisponible → on ignore, mais on renvoie quand même les données
    }

    onData?.(data, { fromCache: false });
  } catch (err) {
    const status = err?.response?.status;
    if (status === 304) return; // OK
    onError?.(err);
    if (import.meta?.env?.DEV) {
      console.warn('[swrFetch] revalidate error for', key, err);
    }
  }
}

/**
 * Purge le cache SWR local (données + etag + ts).
 * Utile quand un appareil reste coincé sur des données "en cache".
 */
export function clearSWRCache(key) {
  if (!key) return;
  try {
    localStorage.removeItem(`swr:${key}`);
    localStorage.removeItem(`swr:${key}:etag`);
    localStorage.removeItem(`swr:${key}:ts`);
  } catch {
    // iOS/private-mode: ignore
  }
}

export default apiClient;
